import {
  computed,
  effect,
  inject,
  signal,
  Resource,
  resource,
  ResourceRef,
  Signal,
  WritableResource,
} from '@angular/core';
import { LogHelper } from '../../logging/log-helper';
import { DashboardContext } from '../state/dashboard-context';
import { DashboardWidgetDataState, injectDashboardWidget } from './dashboard-widget-handle';

/** A reload's indicator shows at least this long, so it reads as a change rather than a flash */
export const MIN_RELOAD_INDICATOR_MS = 300;

export interface DashboardWidgetDataOptions<T> {
  /** Shows the empty state for a loaded value with nothing in it, e.g. `(rows) => rows.length === 0` */
  isEmpty?: (value: T) => boolean;
  emptyMessage?: string;
  errorMessage?: string;
}

export interface DashboardWidgetResourceOptions<T, P> extends DashboardWidgetDataOptions<T> {
  /** What the data depends on, e.g. filter values and settings; undefined leaves the resource idle */
  params?: () => P | undefined;
  loader: (request: { params: P; abortSignal: AbortSignal }) => Promise<T>;
}

/**
 * A widget's data that never blanks once shown: `value` keeps the last loaded value while a later load runs, and
 * `reloading` says one is running, so the widget can put a spinner or bar on what will change.
 */
export interface DashboardWidgetData<T> {
  /** The last loaded value; undefined until the first load finishes */
  readonly value: Signal<T>;
  hasValue(): this is DashboardWidgetData<Exclude<T, undefined>>;
  /** A load is running over a value already shown */
  readonly reloading: Signal<boolean>;
  readonly resource: ResourceRef<T>;
}

/**
 * A `resource()` that reloads on every dashboard refresh and keeps the frame's loading, empty and failed states in
 * step. Later loads keep the last value; show `reloading` on the content that will change.
 */
export function dashboardWidgetResource<T, P = null>(
  options: DashboardWidgetResourceOptions<T, P>,
): DashboardWidgetData<T | undefined> {
  const context = inject(DashboardContext);
  const last = signal<{ value: T } | undefined>(undefined);
  const ref = resource({
    params: () => {
      const params = options.params ? options.params() : (null as P);
      return params === undefined ? undefined : { params, refresh: context.refreshTick() };
    },
    loader: async ({ params, abortSignal }) => {
      const started = Date.now();
      const value = await options.loader({ params: params.params, abortSignal });
      if (last() !== undefined) {
        await delay(MIN_RELOAD_INDICATOR_MS - (Date.now() - started), abortSignal);
      }
      return value;
    },
  });
  trackDashboardWidgetData(ref, options);

  effect(() => {
    if (ref.hasValue()) {
      last.set({ value: ref.value() });
    }
  });
  const loaded = computed(() => (ref.hasValue() ? { value: ref.value() } : last()));
  return {
    value: computed(() => loaded()?.value),
    hasValue: () => loaded() !== undefined,
    reloading: computed(() => ref.isLoading() && loaded() !== undefined),
    resource: ref,
  } as DashboardWidgetData<T | undefined>;
}

/**
 * Keeps the frame in step with any resource, e.g. an `httpResource()`. Read `injectDashboardRefresh()` in its
 * request to reload on refresh.
 */
export function trackDashboardWidgetData<T>(source: Resource<T>, options: DashboardWidgetDataOptions<T> = {}): void {
  const handle = injectDashboardWidget();
  effect(() => handle.setDataState(dataStateOf(source, options, handle.definition.type)));
  // The frame shows a generic message; the cause goes to the console
  effect(() => {
    if (source.status() === 'error') {
      LogHelper.error('DashboardWidget', `'${handle.definition.type}' failed to load its data`, source.error());
    }
  });
}

export function dataStateOf<T>(
  source: Resource<T>,
  options: DashboardWidgetDataOptions<T> = {},
  widgetType?: string,
): DashboardWidgetDataState {
  if (source.isLoading()) {
    return { status: 'loading' };
  }
  if (source.status() === 'error') {
    return failedStateOf(source, options);
  }
  if (!source.hasValue()) {
    return { status: 'ready' };
  }
  try {
    return options.isEmpty?.(source.value()) ? { status: 'empty', message: options.emptyMessage } : { status: 'ready' };
  } catch (error) {
    LogHelper.error('DashboardWidget', `'${widgetType}' isEmpty threw`, error);
    return failedStateOf(source, options);
  }
}

function failedStateOf<T>(source: Resource<T>, options: DashboardWidgetDataOptions<T>): DashboardWidgetDataState {
  const retry = canReload(source)
    ? () => {
        source.reload();
      }
    : undefined;
  return { status: 'failed', message: options.errorMessage, retry };
}

function canReload<T>(source: Resource<T>): source is WritableResource<T> {
  return typeof (source as Partial<WritableResource<T>>).reload === 'function';
}

function delay(ms: number, abortSignal: AbortSignal): Promise<void> {
  return ms <= 0
    ? Promise.resolve()
    : new Promise((resolve) => {
        const timer = setTimeout(resolve, ms);
        abortSignal.addEventListener('abort', () => clearTimeout(timer));
      });
}
